import React from 'react';
import { Table } from 'react-bootstrap';
import { formatCurrency } from 'Main/utils';
import { calculateBill, calculateEscalation } from '@aerialytic/canvas-library';

/**
 * Legacy savings when `utility.billing_type` is null — matches
 * `null-billing-type-savings-calculation.md` (same rules as canvas-library
 * `calculateSavings` for this path):
 *
 * - Annual nominal kWh = sum(monthly production from API / design).
 * - Offset % = floor(annualProduction / annualUsage × 100) from nominal totals.
 * - Each model year: degraded annual kWh (first-year loss, then annual
 *   degradation exponent on nominal annual — see doc §6).
 * - Raw $ savings for that year: default min(production × effectiveRate,
 *   (averageBill − fixedCharge) × 12); if offset_savings AND offset > 100,
 *   use export / UFSC branch from the doc §9; fallbacks if bill/rate missing.
 * - Escalation: `calculateEscalation` (cumulative series); each year's
 *   `annualTotals.savings` is the marginal escalated slice raw×(1+r)^t so
 *   Report can accumulate into `yearlySavings` like other V2 calculators.
 *
 * The verification table keeps the same columns as before. Savings are an
 * **annual** model: each month's "Solar Savings" is 1/12 of that year's raw
 * savings (then escalated for display), with production scaled so monthly kWh
 * sums to the degraded annual total for the selected year.
 */
function computeRawAnnualLegacySavings({
  valueKwh,
  annualUsage,
  offsetPct,
  offsetSavings,
  effectiveRate,
  averageBill,
  fixedCharge,
  utility,
}) {
  if (effectiveRate !== undefined && averageBill !== undefined) {
    if (offsetSavings && offsetPct > 100) {
      const over = valueKwh - annualUsage;
      const ufscAnnual = Number(utility?.ufsc ?? 0) * 12;
      const expR = Number(
        utility?.export_rate ??
          utility?.exportRate ??
          utility?.net_metering_specs?.export_rate ??
          utility?.net_billing_specs?.export_rate ??
          0
      );
      if (over > 0) {
        const exportCredit = over * expR;
        return averageBill * 12 - (ufscAnnual - exportCredit);
      }
      return (
        averageBill * 12 -
        (ufscAnnual + Math.abs(over) * Number(effectiveRate))
      );
    }
    return Math.min(
      valueKwh * Number(effectiveRate),
      (averageBill - Number(fixedCharge)) * 12
    );
  }
  if (effectiveRate !== undefined) {
    return valueKwh * Number(effectiveRate);
  }
  return valueKwh * 0.3;
}

export const calculateLegacySavingsV2 = ({
  monthlyProduction,
  monthlyConsumption,
  systemSize,
  utility,
  panel,
  escalation,
}) => {
  const safeMonthlyProduction = Array.isArray(monthlyProduction)
    ? monthlyProduction.map((v) => Number(v) || 0)
    : new Array(12).fill(0);
  const safeMonthlyConsumption = Array.isArray(monthlyConsumption)
    ? monthlyConsumption.map((v) => Number(v) || 0)
    : new Array(12).fill(0);

  const billResult = calculateBill(utility, false) || {};
  const effectiveRate =
    billResult.effectiveRate ??
    utility?.effectiveRate ??
    utility?.rate_amount;
  const averageBill = billResult.averageBill;
  const fixedCharge = Number(
    billResult.fixedCharge ?? utility?.fixed_charge ?? utility?.fixedCharge ?? 0
  );

  const annualUsage = safeMonthlyConsumption.reduce((a, b) => a + b, 0);
  const monthlyProductionSum = safeMonthlyProduction.reduce(
    (a, b) => a + Number(b),
    0
  );

  const monthlyFixedCharge =
    averageBill !== undefined && effectiveRate !== undefined && annualUsage > 0
      ? Math.max(
        0,
        averageBill - (annualUsage / 12) * Number(effectiveRate)
      )
      : fixedCharge;

  const ufsc = Number(
    utility?.ufsc ??
      utility?.net_metering_specs?.ufsc ??
      utility?.net_billing_specs?.ufsc ??
      monthlyFixedCharge
  );

  const exportRate = Number(
    utility?.export_rate ??
      utility?.net_metering_specs?.export_rate ??
      utility?.net_billing_specs?.export_rate ??
      0
  );

  const offsetSavings = Boolean(
    utility?.offset_savings ?? utility?.net_metering_specs?.offset_savings
  );

  const annualProduction = monthlyProductionSum;

  const offsetPctAnnual =
    annualUsage > 0
      ? Math.floor((annualProduction / annualUsage) * 100)
      : 0;

  const degradationStored = Number(panel?.degradation);
  const degradation =
    (Number.isFinite(degradationStored) && degradationStored >= 0
      ? degradationStored
      : 0.25) / 100;

  const firstYearDegStored =
    panel?.first_year_degradation != null
      ? Number(panel.first_year_degradation) / 100
      : null;
  const firstYearDegradation =
    firstYearDegStored != null && Number.isFinite(firstYearDegStored)
      ? firstYearDegStored
      : 0.02;

  const degradedProductions = Array.from({ length: 30 }, (_, index) => {
    if (index === 0) {
      return annualProduction * (1 - firstYearDegradation);
    }
    return annualProduction * (1 - degradation) ** index;
  });

  const rawAnnualSavingsList = degradedProductions.map((valueKwh) =>
    computeRawAnnualLegacySavings({
      valueKwh,
      annualUsage,
      offsetPct: offsetPctAnnual,
      offsetSavings,
      effectiveRate,
      averageBill,
      fixedCharge,
      utility,
    })
  );

  const escalationRate = typeof escalation === 'number' ? escalation : 0.03;
  const cumulativeSeries = calculateEscalation(
    rawAnnualSavingsList,
    escalationRate
  );

  const yearlyResults = [];

  for (let year = 0; year < 30; year += 1) {
    const escalationFactor = (1 + escalationRate) ** year;
    const degradedAnnual = degradedProductions[year];
    const rawAnnualSavings = rawAnnualSavingsList[year];
    const escalatedMarginalSavings = rawAnnualSavings * escalationFactor;
    const cumulativeSavings = cumulativeSeries[year];

    const overForYear = degradedAnnual - annualUsage;
    let formulaMode = 'legacy_default_cap';
    if (
      !(effectiveRate !== undefined && averageBill !== undefined)
    ) {
      formulaMode =
        effectiveRate !== undefined ? 'fallback_rate' : 'fallback_default';
    } else if (offsetSavings && offsetPctAnnual > 100) {
      formulaMode =
        overForYear > 0
          ? 'legacy_offset_surplus'
          : 'legacy_offset_shortfall';
    }

    let totalExportCreditAnnual = 0;
    let totalShortageChargeAnnual = 0;
    if (offsetSavings && offsetPctAnnual > 100) {
      if (overForYear > 0) {
        totalExportCreditAnnual = overForYear * exportRate;
      } else {
        totalShortageChargeAnnual = Math.abs(overForYear) * Number(effectiveRate);
      }
    }

    const annualNet = degradedAnnual - annualUsage;
    const isAnnualSurplus = annualNet >= 0;

    const annualPreSolarBill =
      averageBill !== undefined ? averageBill * 12 : null;
    const annualUfsc = ufsc * 12;

    const energyAtEffectiveRate =
      effectiveRate !== undefined
        ? degradedAnnual * Number(effectiveRate)
        : null;
    const annualSavingsCap =
      averageBill !== undefined
        ? (averageBill - Number(fixedCharge)) * 12
        : null;

    yearlyResults.push({
      year: year + 1,
      annualTotals: {
        totalProduction: degradedAnnual,
        totalConsumption: annualUsage,
        annualNet,
        isAnnualSurplus,
        formulaMode,
        escalationFactor,
        effectiveRate: effectiveRate !== undefined ? effectiveRate : null,
        averageBill: averageBill !== undefined ? averageBill : null,
        fixedCharge,
        monthlyFixedCharge,
        ufsc,
        exportRate,
        offsetSavings,
        nominalAnnualProduction: annualProduction,
        degradedAnnualProduction: degradedAnnual,
        preSolarBillYearOne: annualPreSolarBill,
        annualUfsc,
        shortageCharge: totalShortageChargeAnnual,
        exportCredit: totalExportCreditAnnual,
        rawAnnualSavings,
        energyAtEffectiveRate,
        annualSavingsCap,
        preSolarBill:
          annualPreSolarBill !== null
            ? annualPreSolarBill * escalationFactor
            : null,
        annualUfscEscalated: annualUfsc * escalationFactor,
        shortageChargeEscalated:
          totalShortageChargeAnnual * escalationFactor,
        exportCreditEscalated: totalExportCreditAnnual * escalationFactor,
        escalatedAnnualSavings: escalatedMarginalSavings,
        postSolarBill:
          annualPreSolarBill !== null
            ? (annualPreSolarBill - rawAnnualSavings) * escalationFactor
            : null,
        cumulativeSavings,
        savings: escalatedMarginalSavings,
        preSolarCost:
          annualPreSolarBill !== null
            ? annualPreSolarBill * escalationFactor
            : null,
      },
    });
  }

  return {
    yearlyResults,
    metadata: {
      mode: 'legacy',
      effectiveRate: effectiveRate !== undefined ? effectiveRate : null,
      averageBill: averageBill !== undefined ? averageBill : null,
      fixedCharge,
      monthlyFixedCharge,
      ufsc,
      ufscEqualsFixedCharge: Math.abs(ufsc - monthlyFixedCharge) < 1e-6,
      exportRate,
      offsetSavings,
      escalationRate: escalationRate * 100,
      annualUsage,
      annualProduction,
      offsetPct: offsetPctAnnual,
      systemSize,
      utility,
      firstYearDegradationPct:
        panel?.first_year_degradation != null
          ? Number(panel.first_year_degradation)
          : firstYearDegradation * 100,
      annualDegradationPct: Number(panel?.degradation ?? 0.25),
    },
  };
};

const fmtKwh = (value) => Number(value || 0).toFixed(2);
const fmtRate = (value) =>
  value === null || value === undefined ? '—' : `$${Number(value).toFixed(4)}`;

function formatLegacyFormulaMode(mode) {
  const labels = {
    legacy_default_cap: 'Default cap (min)',
    legacy_offset_surplus: 'offset_savings · surplus',
    legacy_offset_shortfall: 'offset_savings · shortfall',
    fallback_rate: 'Fallback (kWh × rate)',
    fallback_default: 'Fallback ($0.30/kWh)',
  };
  return labels[mode] || mode;
}

function buildLegacyYearTooltip(ats, meta) {
  if (ats.formulaMode === 'fallback_rate' || ats.formulaMode === 'fallback_default') {
    const note =
      ats.formulaMode === 'fallback_rate'
        ? 'averageBill missing — raw = degraded annual kWh × effectiveRate'
        : 'effectiveRate missing — raw = degraded annual × $0.30';
    return [
      '── Fallback (annual) ──',
      note,
      `Raw (year-1 $): ${formatCurrency(ats.rawAnnualSavings)}`,
      `Marginal escalated: ${formatCurrency(ats.escalatedAnnualSavings)}`,
    ].join('\n');
  }
  if (ats.formulaMode === 'legacy_offset_surplus') {
    const v = ats.degradedAnnualProduction;
    const u = ats.totalConsumption;
    const over = v - u;
    return [
      '── offset_savings + nominal offset > 100% (doc §9.1) ──',
      `Degraded annual kWh: ${fmtKwh(v)}`,
      `Annual usage kWh: ${fmtKwh(u)}`,
      `Surplus kWh: ${fmtKwh(over)}`,
      `Export credit: ${formatCurrency(ats.exportCredit)}`,
      `UFSC × 12: ${formatCurrency(ats.annualUfsc)}`,
      `Raw = avgBill×12 − (UFSC×12 − export) = ${formatCurrency(ats.rawAnnualSavings)}`,
    ].join('\n');
  }
  if (ats.formulaMode === 'legacy_offset_shortfall') {
    const v = ats.degradedAnnualProduction;
    const u = ats.totalConsumption;
    const short = Math.abs(v - u);
    return [
      '── offset_savings · degraded year at/below usage (doc §9.2) ──',
      `Degraded kWh: ${fmtKwh(v)} · Shortfall: ${fmtKwh(short)}`,
      `Raw = avgBill×12 − (UFSC×12 + shortfall×rate) = ${formatCurrency(
        ats.rawAnnualSavings
      )}`,
    ].join('\n');
  }
  const capLine =
    ats.energyAtEffectiveRate != null && ats.annualSavingsCap != null
      ? `min(${formatCurrency(ats.energyAtEffectiveRate)} energy value, ${formatCurrency(ats.annualSavingsCap)} bill cap)`
      : 'min(degraded kWh × rate, (avgBill − fixed)×12)';
  return [
    '── Default cap (doc §7.1) ──',
    `Nominal annual kWh (Y1 basis): ${fmtKwh(
      ats.nominalAnnualProduction ?? meta.annualProduction
    )}`,
    `Degraded annual kWh: ${fmtKwh(ats.degradedAnnualProduction)}`,
    capLine,
    `Raw savings (year-1 $): ${formatCurrency(ats.rawAnnualSavings)}`,
  ].join('\n');
}

/**
 * Verification table for legacy savings when `utility.billing_type` is null.
 * One row per model year — annual production, degradation, raw savings,
 * escalation, and cumulative series (matches `calculateEscalation` + Report).
 */
export const LegacySavingsTable = ({ calculationResults }) => {
  if (!calculationResults?.yearlyResults?.length) {
    return <div>No calculation data available</div>;
  }

  const { yearlyResults, metadata } = calculationResults;

  const thirtyYearTotals = yearlyResults.reduce(
    (acc, year) => ({
      totalDegradedKwh: acc.totalDegradedKwh + year.annualTotals.totalProduction,
      rawSavings: acc.rawSavings + year.annualTotals.rawAnnualSavings,
      escalatedSavings:
        acc.escalatedSavings + year.annualTotals.escalatedAnnualSavings,
      preSolarCost:
        acc.preSolarCost + (year.annualTotals.preSolarCost || 0),
      shortageCharge:
        acc.shortageCharge + year.annualTotals.shortageChargeEscalated,
      exportCredit:
        acc.exportCredit + year.annualTotals.exportCreditEscalated,
    }),
    {
      totalDegradedKwh: 0,
      rawSavings: 0,
      escalatedSavings: 0,
      preSolarCost: 0,
      shortageCharge: 0,
      exportCredit: 0,
    }
  );

  const cumulative30Year =
    yearlyResults[yearlyResults.length - 1].annualTotals.cumulativeSavings;

  return (
    <div className="table-responsive">
      <div className="mb-3">
        <h6 className="mb-2">Legacy savings (annual model)</h6>
        <p className="small text-muted mb-2">
          Dollar savings use <strong>annual</strong> kWh (sum of API monthly
          production = nominal year-1 total), then panel degradation per model
          year. No monthly bill netting — only the inputs below and the formulas
          in <code>null-billing-type-savings-calculation.md</code>.
        </p>
        <div className="row small">
          <div className="col-md-6">
            <div>
              <strong>Effective rate:</strong> {fmtRate(metadata.effectiveRate)}
              /kWh
            </div>
            <div>
              <strong>Avg monthly bill:</strong>{' '}
              {metadata.averageBill != null
                ? formatCurrency(metadata.averageBill)
                : '—'}
            </div>
            <div>
              <strong>Pre-solar fixed (derived):</strong>{' '}
              {formatCurrency(metadata.monthlyFixedCharge)}/mo
            </div>
            <div>
              <strong>UFSC:</strong> {formatCurrency(metadata.ufsc)}/mo
            </div>
          </div>
          <div className="col-md-6">
            <div>
              <strong>Nominal annual production:</strong>{' '}
              {fmtKwh(metadata.annualProduction)} kWh
            </div>
            <div>
              <strong>Annual usage:</strong> {fmtKwh(metadata.annualUsage)} kWh
            </div>
            <div>
              <strong>Nominal offset (Y1):</strong> {metadata.offsetPct}%
            </div>
            <div>
              <strong>Escalation:</strong> {metadata.escalationRate}% / yr
            </div>
            <div>
              <strong>Degradation:</strong> first-year{' '}
              {metadata.firstYearDegradationPct?.toFixed?.(2) ?? '—'}% · annual{' '}
              {metadata.annualDegradationPct?.toFixed?.(2) ?? '—'}%
            </div>
            <div>
              <strong>offset_savings:</strong>{' '}
              {metadata.offsetSavings ? 'on' : 'off'}
              {metadata.offsetSavings && (
                <span className="text-muted">
                  {' '}
                  · export {fmtRate(metadata.exportRate)}/kWh
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Table striped bordered hover size="sm" className="text-nowrap">
        <thead>
          <tr>
            <th>Year</th>
            <th>Degraded production (kWh)</th>
            <th>Usage (kWh)</th>
            <th>Net (kWh)</th>
            <th>Mode</th>
            <th>Energy × rate ($)</th>
            <th>Bill cap ($)</th>
            <th>Raw savings (y1 $)</th>
            <th>Esc. factor</th>
            <th>Marginal ($)</th>
            <th>Cumulative ($)</th>
            <th>Pre-solar bill ($)</th>
            <th>Post-solar ($)</th>
          </tr>
        </thead>
        <tbody>
          {yearlyResults.map((yr) => {
            const ats = yr.annualTotals;
            const netColor = ats.annualNet >= 0 ? 'green' : 'inherit';
            const marginalColor =
              ats.escalatedAnnualSavings >= 0 ? 'green' : 'red';
            const cumulativeColor =
              ats.cumulativeSavings >= 0 ? 'green' : 'red';
            const rowTip = buildLegacyYearTooltip(ats, metadata);
            return (
              <tr key={yr.year}>
                <td>{yr.year}</td>
                <td title="Model-year AC kWh after first-year / annual degradation (doc §6)">
                  {fmtKwh(ats.totalProduction)}
                </td>
                <td title="Annual consumption from utility usage (unchanged each year)">
                  {fmtKwh(ats.totalConsumption)}
                </td>
                <td style={{ color: netColor }}>{fmtKwh(ats.annualNet)}</td>
                <td title={rowTip}>
                  {formatLegacyFormulaMode(ats.formulaMode)}
                </td>
                <td title="Degraded kWh × effective rate (before min with cap)">
                  {ats.energyAtEffectiveRate != null
                    ? formatCurrency(ats.energyAtEffectiveRate)
                    : '—'}
                </td>
                <td title="(Avg monthly bill − fixed charge) × 12">
                  {ats.annualSavingsCap != null
                    ? formatCurrency(ats.annualSavingsCap)
                    : '—'}
                </td>
                <td title={rowTip}>{formatCurrency(ats.rawAnnualSavings)}</td>
                <td title="(1 + escalation)^(year − 1)">
                  {ats.escalationFactor.toFixed(4)}
                </td>
                <td
                  title="Raw × esc. factor — this value is summed in Report to match calculateEscalation"
                  style={{ color: marginalColor }}
                >
                  {formatCurrency(ats.escalatedAnnualSavings)}
                </td>
                <td
                  title="calculateEscalation(raw series) through this year"
                  style={{ color: cumulativeColor }}
                >
                  {formatCurrency(ats.cumulativeSavings)}
                </td>
                <td>
                  {ats.preSolarBill != null
                    ? formatCurrency(ats.preSolarBill)
                    : '—'}
                </td>
                <td>
                  {ats.postSolarBill != null
                    ? formatCurrency(ats.postSolarBill)
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="mt-3">
        <h6>30-year totals</h6>
        <div className="row small">
          <div className="col-md-4">
            <div>
              <strong>Σ Degraded production (30 yrs):</strong>{' '}
              {fmtKwh(thirtyYearTotals.totalDegradedKwh)} kWh
            </div>
            <div>
              <strong>Annual usage (each year):</strong>{' '}
              {fmtKwh(metadata.annualUsage)} kWh
            </div>
          </div>
          <div className="col-md-4">
            <div>
              <strong>Σ Raw savings (year-1 $):</strong>{' '}
              {formatCurrency(thirtyYearTotals.rawSavings)}
            </div>
            <div>
              <strong>Σ Marginal escalated:</strong>{' '}
              {formatCurrency(thirtyYearTotals.escalatedSavings)}
            </div>
            {metadata.offsetSavings && thirtyYearTotals.exportCredit > 0 && (
              <div className="text-success">
                <strong>Σ Export credits (escalated):</strong>{' '}
                {formatCurrency(thirtyYearTotals.exportCredit)}
              </div>
            )}
            {thirtyYearTotals.shortageCharge > 0 && (
              <div className="text-warning">
                <strong>Σ Shortfall energy charges (escalated):</strong>{' '}
                {formatCurrency(thirtyYearTotals.shortageCharge)}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <div>
              <strong>Σ Pre-solar bills (escalated):</strong>{' '}
              {formatCurrency(thirtyYearTotals.preSolarCost)}
            </div>
            <div className="text-success">
              <strong>Final cumulative savings:</strong>{' '}
              {formatCurrency(cumulative30Year)}
            </div>
            <div className="text-muted">
              Same cumulative series passed to <code>calculateCosts</code> after
              Report aggregates marginal rows.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegacySavingsTable;
